import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import {
  FaBoxOpen,
  FaChevronRight,
  FaCheckCircle,
  FaShoppingCart,
  FaEnvelope,
  FaCloud,
  FaMobileAlt,
  FaWhatsapp,
  FaServer,
  FaSpinner,
} from "react-icons/fa";

import "./Products.css";

/* =========================================================
   API
========================================================= */

import { BASE_URL } from "../../Config/config";

const API_URL = `${BASE_URL}/products.php`;

const IMAGE_URL = `${BASE_URL}/`;
const DEFAULT_PRODUCT_ICON = "FaBoxOpen";

const DynamicFaIcon = ({ name, ...props }) => {
  const IconComponent =
    FaIcons[name] || FaIcons[DEFAULT_PRODUCT_ICON] || FaIcons.FaBoxOpen;

  return <IconComponent {...props} />;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function Products() {
  const { product, category } = useParams();

  /* =======================================================
     API DATA
  ======================================================= */

  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /* =======================================================
     ACTIVE STATES
  ======================================================= */

  const [activeProduct, setActiveProduct] = useState("");

  const [activeCategory, setActiveCategory] = useState("");

  /* =======================================================
     IMAGE URL
  ======================================================= */

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://") ||
      image.startsWith("blob:")
    ) {
      return image;
    }

    return `${IMAGE_URL}${image}`;
  };

  /* =======================================================
     FETCH PRODUCTS
  ======================================================= */

  const fetchProducts = async () => {
    try {
      setLoading(true);

      setError("");

      const response = await fetch(`${API_URL}?action=get`);

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data = await response.json();

      if (!data.status) {
        throw new Error(data.message || "Failed to fetch products.");
      }

      const apiProducts = Array.isArray(data.data) ? data.data : [];

      /* ---------------------------------------------------
         ONLY ACTIVE PRODUCTS
      --------------------------------------------------- */

      const activeProducts = apiProducts
        .filter((item) => Boolean(Number(item.status)))
        .map((item) => ({
          ...item,

          name: item.name || "",

          description: item.description || "",

          // IMPORTANT:
          // Use icon coming from database
          icon: item.icon || DEFAULT_PRODUCT_ICON,

          modules: Array.isArray(item.modules)
            ? item.modules.map((module) => ({
                ...module,

                name: module.name || "",

                description: module.description || "",

                // Keep module icon from database too
                icon: module.icon || "FaLayerGroup",

                subcategories: Array.isArray(module.subcategories)
                  ? module.subcategories
                  : [],
              }))
            : [],
        }));

      setProducts(activeProducts);

      /* ---------------------------------------------------
         SET INITIAL PRODUCT
      --------------------------------------------------- */

      if (activeProducts.length > 0) {
        const productMap = {
          "tally-prime": "Tally Prime",
          miracle: "Miracle",
          busy: "BUSY",
          "odoo-erp": "Odoo ERP",
          "ottu-hrms": "Ottu HRMS",
        };

        let selectedProductName = product ? productMap[product] || product : "";

        let selectedProduct = activeProducts.find(
          (item) =>
            item.name.toLowerCase() ===
            String(selectedProductName).toLowerCase(),
        );

        if (!selectedProduct) {
          selectedProduct = activeProducts[0];
        }

        setActiveProduct(selectedProduct.name);

        /* -------------------------------------------------
           SET INITIAL CATEGORY
        ------------------------------------------------- */

        const modules = selectedProduct.modules || [];

        if (modules.length > 0) {
          const categoryMap = {
            "new-license": "New License",
            renewal: "Renewal",
            upgrade: "Upgrade",
            "tallyprime-server": "TallyPrime Server",
            "tally-on-mobile": "Tally on Mobile",
            "tally-on-cloud": "Tally on Cloud",
            "tally-on-whatsapp": "Tally on WhatsApp",

            amc: "AMC",
            "cloud-solutions": "Cloud Solutions",

            cloud: "Cloud",
            mobile: "Mobile",

            implementation: "Implementation",
            enterprise: "Enterprise",
            customization: "Customization",
            support: "Support",

            payroll: "Payroll",
            attendance: "Attendance",
            hrms: "HRMS",
          };

          let selectedCategoryName = category
            ? categoryMap[category] || category
            : "";

          let selectedModule = modules.find(
            (item) =>
              item.name.toLowerCase() ===
              String(selectedCategoryName).toLowerCase(),
          );

          if (!selectedModule) {
            selectedModule = modules[0];
          }

          setActiveCategory(selectedModule?.name || "");
        } else {
          setActiveCategory("");
        }
      } else {
        setActiveProduct("");

        setActiveCategory("");
      }
    } catch (err) {
      console.error("FETCH PRODUCTS ERROR:", err);

      setError(err.message || "Unable to load products.");

      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    fetchProducts();
  }, []);
  useEffect(() => {
    if (!products.length) return;

    const productMap = {
      "tally-prime": "Tally Prime",
      miracle: "Miracle",
      busy: "BUSY",
      "odoo-erp": "Odoo ERP",
      "ottu-hrms": "Ottu HRMS",
    };

    const categoryMap = {
      "new-license": "New License",
      renewal: "Renewal",
      upgrade: "Upgrade",
      "tallyprime-server": "TallyPrime Server",
      "tally-on-mobile": "Tally on Mobile",
      "tally-on-cloud": "Tally on Cloud",
      "tally-on-whatsapp": "Tally on WhatsApp",
      amc: "AMC",
      "cloud-solutions": "Cloud Solutions",
      cloud: "Cloud",
      mobile: "Mobile",
      implementation: "Implementation",
      enterprise: "Enterprise",
      customization: "Customization",
      support: "Support",
      payroll: "Payroll",
      attendance: "Attendance",
      hrms: "HRMS",
    };

    // -----------------------------
    // Find product from URL
    // -----------------------------

    const selectedProductName = product ? productMap[product] || product : "";

    let selectedProduct = products.find(
      (item) =>
        item.name.toLowerCase() === String(selectedProductName).toLowerCase(),
    );

    // If URL does not contain product, use first product
    if (!selectedProduct) {
      selectedProduct = products[0];
    }

    setActiveProduct(selectedProduct?.name || "");

    // -----------------------------
    // Find category from URL
    // -----------------------------

    const modules = selectedProduct?.modules || [];

    if (modules.length === 0) {
      setActiveCategory("");
      return;
    }

    const selectedCategoryName = category
      ? categoryMap[category] || category
      : "";

    let selectedModule = modules.find(
      (module) =>
        module.name.toLowerCase() ===
        String(selectedCategoryName).toLowerCase(),
    );

    // If category not found, use first module
    if (!selectedModule) {
      selectedModule = modules[0];
    }

    setActiveCategory(selectedModule?.name || "");
  }, [product, category, products]);
  /* =======================================================
     CURRENT PRODUCT
  ======================================================= */

  const currentProduct = useMemo(() => {
    return products.find((item) => item.name === activeProduct);
  }, [products, activeProduct]);

  /* =======================================================
     CURRENT MODULE / CATEGORY
  ======================================================= */

  const currentModule = useMemo(() => {
    if (!currentProduct) {
      return null;
    }

    return (
      currentProduct.modules?.find(
        (module) => module.name === activeCategory,
      ) || null
    );
  }, [currentProduct, activeCategory]);

  /* =======================================================
     CURRENT PLANS
  ======================================================= */

  const currentPlans = currentModule?.subcategories || [];

  /* =======================================================
     CHANGE PRODUCT
  ======================================================= */

  const changeProduct = (selectedProduct) => {
    setActiveProduct(selectedProduct.name);

    const firstModule = selectedProduct.modules?.[0];

    setActiveCategory(firstModule?.name || "");
  };

  /* =======================================================
     CHANGE CATEGORY
  ======================================================= */

  const changeCategory = (module) => {
    setActiveCategory(module.name);
  };

  /* =======================================================
     CONTACT LINK
  ======================================================= */
  const getContactLink = (plan) => {
    const productName = activeProduct || "";
    const moduleName = activeCategory || "";
    const planName = plan.name || "";
    const price = plan.price || "Price on request";

    const selectedModule = `${productName} - ${moduleName} - ${planName}`;

    const automaticMessage =
      `Hello, I am interested in ${selectedModule}. ` +
      `Please provide more details, pricing and available options.`;

    const params = new URLSearchParams({
      product: productName,
      module: moduleName,
      title: selectedModule,
      code: planName,
      price: price,
      plan: planName,
      message: automaticMessage,
    });

    return `/contact?${params.toString()}`;
  };
  /* =======================================================
     PLAN ICON
  ======================================================= */

  const getPlanIcon = (plan) => {
    const categoryName = String(activeCategory || "").toLowerCase();

    const planName = String(plan?.name || "").toLowerCase();

    if (categoryName.includes("cloud") || planName.includes("cloud")) {
      return <FaCloud />;
    }

    if (categoryName.includes("mobile") || planName.includes("mobile")) {
      return <FaMobileAlt />;
    }

    if (categoryName.includes("whatsapp") || planName.includes("whatsapp")) {
      return <FaWhatsapp />;
    }

    if (categoryName.includes("server") || planName.includes("server")) {
      return <FaServer />;
    }

    return <FaCheckCircle />;
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <main className="products-page">
        <section className="products-loading">
          <FaSpinner className="products-loading-spinner" />

          <h3>Loading Products...</h3>

          <p>Please wait while we load our products and solutions.</p>
        </section>
      </main>
    );
  }

  /* =======================================================
     ERROR
  ======================================================= */

  if (error) {
    return (
      <main className="products-page">
        <section className="products-error">
          <FaBoxOpen />

          <h2>Unable to Load Products</h2>

          <p>{error}</p>

          <button
            type="button"
            onClick={fetchProducts}
            className="retry-products-btn"
          >
            Try Again
          </button>
        </section>
      </main>
    );
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <main className="products-page">
      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>

        <div className="section-container contact-hero-content">
          <span className="section-label">OUR PRODUCTS & SOLUTIONS</span>

          <h1>
            Software Solutions
            <span> For Your Business</span>
          </h1>

          <p>
            Explore accounting, ERP, HRMS and business automation solutions
            designed to simplify your operations and help your business grow.
          </p>
        </div>
      </section>

      {/* =====================================================
          PRODUCT AREA
      ===================================================== */}

      <section className="products-main">
        <div className="section-container">
          <div className="products-layout">
            {/* =================================================
                LEFT SIDEBAR
            ================================================= */}

            <aside className="products-sidebar">
              <div className="sidebar-title">
                <span>OUR PRODUCTS</span>

                <h3>Choose Solution</h3>
              </div>

              <div className="product-menu">
                {products.length === 0 ? (
                  <div className="no-products-sidebar">
                    No products available.
                  </div>
                ) : (
                  products.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className={`product-menu-item ${
                        activeProduct === item.name ? "active" : ""
                      }`}
                      onClick={() => changeProduct(item)}
                    >
                      <div className="product-menu-icon">
                        <DynamicFaIcon name={item.icon} />
                      </div>

                      <div className="product-menu-text">
                        <strong>{item.name}</strong>

                        <small>
                          {item.description || "Business software solution"}
                        </small>
                      </div>

                      <FaChevronRight className="product-menu-arrow" />
                    </button>
                  ))
                )}
              </div>
            </aside>

            {/* =================================================
                RIGHT CONTENT
            ================================================= */}

            <div className="products-content">
              {!currentProduct ? (
                <div className="products-empty">
                  <FaBoxOpen />

                  <h3>No Product Selected</h3>

                  <p>Please select a product from the list.</p>
                </div>
              ) : (
                <>
                  {/* ==========================================
                      PRODUCT HEADER
                  ========================================== */}

                  <div className="selected-product-header">
                    <div className="selected-product-icon">
                      <DynamicFaIcon name={currentProduct.icon} />
                    </div>

                    <div>
                      <span className="content-label">
                        {currentProduct.name.toUpperCase()}
                      </span>

                      <h2>{currentProduct.name}</h2>

                      <p>
                        {currentProduct.description ||
                          "Complete business software solution."}
                      </p>
                    </div>
                  </div>

                  {/* ==========================================
                      SUB CATEGORIES / MODULES
                  ========================================== */}

                  {currentProduct.modules?.length > 0 && (
                    <div className="subcategory-section">
                      <div className="subcategory-title">
                        <span>SUB PRODUCTS</span>

                        <h3>Choose a Category</h3>
                      </div>

                      <div className="subcategory-tabs">
                        {currentProduct.modules.map((module) => (
                          <button
                            type="button"
                            key={module.id}
                            className={
                              activeCategory === module.name ? "active" : ""
                            }
                            onClick={() => changeCategory(module)}
                          >
                            <DynamicFaIcon name={module.icon} />
                            <span>{module.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ==========================================
                      CUSTOM QUOTE
                  ========================================== */}

                  <div className="quote-box">
                    <div className="quote-icon">
                      <FaCheckCircle />
                    </div>

                    <div>
                      <strong>Need a custom quote or bundle?</strong>

                      <p>
                        Talk to our experts for customized pricing and business
                        requirements.
                      </p>
                    </div>

                    <a href="/contact" className="quote-link">
                      Talk to Us
                      <FaChevronRight />
                    </a>
                  </div>

                  {/* ==========================================
                      PLANS HEADER
                  ========================================== */}

                  <div className="plans-header">
                    <div>
                      <span>{currentProduct.name}</span>

                      <h2>{activeCategory || "Available Solutions"}</h2>
                    </div>

                    <p>
                      {currentPlans.length}{" "}
                      {currentPlans.length === 1 ? "Plan" : "Plans"}
                    </p>
                  </div>

                  {/* ==========================================
                      PLANS
                  ========================================== */}

                  {currentPlans.length === 0 ? (
                    <div className="plans-empty">
                      <FaBoxOpen />

                      <h3>No plans available</h3>

                      <p>
                        No subcategories have been added under this category
                        yet.
                      </p>

                      <a href="/contact" className="empty-contact-btn">
                        Enquire Now
                      </a>
                    </div>
                  ) : (
                    <div className="plans-grid">
                      {currentPlans.map((plan, index) => (
                        <div className="plan-card" key={plan.id || index}>
                          {/* =================================
                              IMAGE
                          ================================= */}

                          <div className="plan-image-wrapper">
                            {plan.image ? (
                              <img
                                src={getImageUrl(plan.image)}
                                alt={plan.name}
                                className="plan-image"
                              />
                            ) : (
                              <div className="plan-no-image">
                                {getPlanIcon(plan)}
                              </div>
                            )}
                          </div>

                          {/* =================================
                              TOP
                          ================================= */}

                          <div className="plan-card-top">
                            <div className="plan-number">
                              {String(index + 1).padStart(2, "0")}
                            </div>

                            <div className="plan-icon">{getPlanIcon(plan)}</div>
                          </div>

                          {/* =================================
                              NAME
                          ================================= */}

                          <h3>{plan.name}</h3>

                          {/* =================================
                              DESCRIPTION
                          ================================= */}

                          <p className="plan-description">
                            {plan.description ||
                              "Professional business solution designed to meet your requirements."}
                          </p>

                          {/* =================================
                              PRICE
                          ================================= */}

                          <div className="plan-price-label">PRICE</div>

                          <div className="plan-price">
                            {plan.price || "Price on request"}
                          </div>

                          {/* =================================
                              DIVIDER
                          ================================= */}

                          <div className="plan-divider"></div>

                          {/* =================================
                              ACTIONS
                          ================================= */}

                          <div className="plan-actions">
                            <a href={getContactLink(plan)} className="buy-btn">
                              <FaShoppingCart />
                              Buy Now
                            </a>

                            <a href="/contact" className="enquire-btn">
                              <FaEnvelope />
                              Enquire
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
